/**
 * PotentialCampaignsPage (formerly BucketListPage)
 * Displays saved opportunities, experiments, AND marketing plans
 * 
 * Top-level navigation item for managing AI-identified campaign opportunities
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  Loader2, Bookmark, Trash2, Target, Beaker, ExternalLink,
  FolderOpen, ArrowRight, ArrowLeft, Calendar, Sparkles, Plus, Globe,
  MoreVertical, Pencil
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import PlanCreateModal from './plans/PlanCreateModal';
import { plansApi } from './plans/api/plansApi';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BucketListPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [plans, setPlans] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load bucket list items and plans in parallel
      const [bucketResponse, plansData] = await Promise.all([
        axios.get(`${API}/api/bucket-list`),
        plansApi.getAll()
      ]);
      setItems(bucketResponse.data || []);
      setPlans(plansData || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load potential campaigns');
    } finally {
      setLoading(false);
    }
  };

  const removeItem = async (itemId) => {
    try {
      await axios.delete(`${API}/api/bucket-list/${itemId}`);
      setItems(prev => prev.filter(i => i.id !== itemId));
      toast.success('Removed from potential campaigns');
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const deletePlan = async (planId) => {
    try {
      await plansApi.delete(planId);
      setPlans(prev => prev.filter(p => p.id !== planId));
      toast.success('Plan deleted');
    } catch (error) {
      toast.error('Failed to delete plan');
    }
  };

  const handlePlanCreated = (plan) => {
    // Navigate to the new plan
    navigate(`/plans/${plan.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </div>
    );
  }

  const opportunities = items.filter(i => i.item_type === 'opportunity');
  const experiments = items.filter(i => i.item_type === 'experiment');
  const hasContent = items.length > 0 || plans.length > 0;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/dashboard')}
          className="mb-4 gap-2 text-muted-foreground hover:text-foreground"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Button>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-purple-500" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Potential Campaigns</h1>
                <p className="text-muted-foreground">
                  Marketing plans and AI-identified opportunities
                </p>
              </div>
            </div>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-purple-500 hover:bg-purple-600 text-white gap-2"
              data-testid="new-plan-btn"
            >
              <Plus className="w-4 h-4" />
              New Plan
            </Button>
          </div>
        </div>

        {!hasContent ? (
          <EmptyState onCreatePlan={() => setShowCreateModal(true)} />
        ) : (
          <div className="space-y-8">
            {/* Plans Section */}
            {plans.length > 0 && (
              <section>
                <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-500" />
                  Marketing Plans ({plans.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {plans.map(plan => (
                    <PlanCard 
                      key={plan.id} 
                      plan={plan}
                      onOpen={() => navigate(`/plans/${plan.id}`)}
                      onDelete={() => deletePlan(plan.id)}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Opportunities Section */}
            {opportunities.length > 0 && (
              <section>
                <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-primary" />
                  Saved Opportunities ({opportunities.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {opportunities.map(item => (
                    <BucketItem 
                      key={item.id} 
                      item={item} 
                      onRemove={removeItem}
                      onNavigate={() => navigate(`/assets/${item.asset_id}`)}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Experiments Section */}
            {experiments.length > 0 && (
              <section>
                <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Beaker className="w-5 h-5 text-blue-500" />
                  Saved Experiments ({experiments.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {experiments.map(item => (
                    <BucketItem 
                      key={item.id} 
                      item={item} 
                      onRemove={removeItem}
                      onNavigate={() => navigate(`/assets/${item.asset_id}`)}
                    />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>

      {/* Create Plan Modal */}
      <PlanCreateModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        onSuccess={handlePlanCreated}
      />
    </div>
  );
}

function EmptyState({ onCreatePlan }) {
  const navigate = useNavigate();
  
  return (
    <div className="text-center py-16 bg-surface border border-border rounded-2xl">
      <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-purple-500/10 flex items-center justify-center">
        <Sparkles className="w-10 h-10 text-purple-500" />
      </div>
      <h2 className="text-xl font-semibold text-foreground mb-2">No Potential Campaigns Yet</h2>
      <p className="text-muted-foreground mb-6 max-w-md mx-auto">
        Create a marketing plan to start building campaigns, or save opportunities from your asset analyses.
      </p>
      <div className="flex items-center justify-center gap-3">
        <Button 
          onClick={onCreatePlan} 
          className="gap-2 bg-purple-500 hover:bg-purple-600 text-white"
          data-testid="empty-new-plan-btn"
        >
          <Plus className="w-4 h-4" />
          New Plan
        </Button>
        <Button variant="outline" onClick={() => navigate('/assets')} className="gap-2">
          <ArrowRight className="w-4 h-4" />
          Go to Assets
        </Button>
      </div>
    </div>
  );
}

function PlanCard({ plan, onOpen, onDelete }) {
  const createdDate = new Date(plan.created_at).toLocaleDateString();
  const step0 = plan.plan_state?.steps?.find(s => s.id === 'step0_goal_offer');
  const isStep0Done = step0?.status === 'completed';
  
  // Get goal and offer labels
  const goal = step0?.data?.primary_goal?.replace('custom_', '') || null;
  const offer = step0?.data?.offer_style?.replace('custom_', '') || null;
  
  return (
    <div 
      className="bg-surface border border-border rounded-xl p-4 hover:border-purple-300 dark:hover:border-purple-700 transition-colors group cursor-pointer"
      onClick={onOpen}
      data-testid={`plan-card-${plan.id}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5 text-purple-500" />
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-foreground truncate">{plan.name}</h3>
            <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
              <Globe className="w-3 h-3" />
              <span className="truncate">{plan.asset_name || 'Unknown Asset'}</span>
            </div>
          </div>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100">
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onOpen(); }}>
              <Pencil className="w-4 h-4 mr-2" />
              Open Plan
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="text-destructive"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      {/* Step 0 Summary */}
      {isStep0Done && (
        <div className="mt-3 pt-3 border-t border-border">
          <div className="flex flex-wrap gap-2">
            {goal && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-600 dark:text-purple-400 text-xs">
                <Target className="w-3 h-3" />
                {goal}
              </span>
            )}
            {offer && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 text-xs">
                {offer}
              </span>
            )}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
        <Calendar className="w-3 h-3" />
        <span>Created {createdDate}</span>
        {!isStep0Done && (
          <span className="ml-auto px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400">
            Draft
          </span>
        )}
      </div>
    </div>
  );
}

function BucketItem({ item, onRemove, onNavigate }) {
  const isExperiment = item.item_type === 'experiment';
  const savedDate = new Date(item.saved_at).toLocaleDateString();
  
  return (
    <div className="bg-surface border border-border rounded-xl p-4 hover:border-primary/30 transition-colors group">
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
          isExperiment ? 'bg-blue-500/10' : 'bg-primary/10'
        }`}>
          {isExperiment ? (
            <Beaker className="w-5 h-5 text-blue-500" />
          ) : (
            <Target className="w-5 h-5 text-primary" />
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-foreground truncate">
            {isExperiment ? item.experiment_title : item.opportunity_name}
          </h3>
          {isExperiment && item.opportunity_name && (
            <p className="text-xs text-muted-foreground truncate">
              From: {item.opportunity_name}
            </p>
          )}
          <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
            <Calendar className="w-3 h-3" />
            <span>Saved {savedDate}</span>
          </div>
        </div>
      </div>
      
      {item.notes && (
        <p className="text-sm text-muted-foreground mt-3 line-clamp-2">
          {item.notes}
        </p>
      )}
      
      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-border">
        <Button 
          variant="outline" 
          size="sm" 
          className="flex-1 gap-1.5"
          onClick={onNavigate}
        >
          <ExternalLink className="w-3.5 h-3.5" />
          View Asset
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive hover:text-destructive hover:bg-destructive/10"
          onClick={() => onRemove(item.id)}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
